import { Checkout, TrialType } from '../../../common/types/checkout';
import { formatCurrency } from '../../../common/utils/format-currency';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Angle } from '../../atoms/angle/angle.atom';
import { MordenCard } from '../../atoms/modern-card/modern-card';
import { useTranslation } from 'react-i18next';
import { BillingPeriodUnit } from '../../../common/types/enums/billing-period-unit';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

const MobileSummaryBar = styled(MordenCard)`
  border-radius: var(--radius);
  padding: 0;
  overflow: hidden;

  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }

  .total {
    display: flex;
    gap: 10px;
    align-items: baseline;

    .old {
      text-decoration: line-through;
      color: #d23c3c;
      opacity: 0.8;
      font-size: 14px;
    }

    .now {
      font-size: 20px;
      font-weight: 700;
    }

    .recurring-preview {
      display: grid;
      gap: 2px;
      text-align: right;

      .primary {
        font-size: 16px;
        font-weight: 700;
        line-height: 1.1;
      }

      .secondary {
        font-size: 12px;
        color: var(--muted);
        font-weight: 600;
        line-height: 1.1;
      }
    }
  }

  .body {
    padding: 12px 12px 4px 12px;
  }
`;

type ModernSummaryProps = {
  open: boolean;
  setOpen: (b: boolean) => void;
  qty: number;
  setQty: (n: number) => void;
  checkout: Checkout;
  uiOrderBumpsAplicados?: Array<{
    id: string;
    produtoOferta?: { nome?: string };
    precos?: Array<{ valor: number }>;
  }>;
  uiSubtotal?: number;
  discount: number;
};

export const ModernOrderSummary: React.FC<ModernSummaryProps> = ({
  open,
  setOpen,
  qty,
  setQty,
  checkout,
  uiOrderBumpsAplicados,
  uiSubtotal,
  discount,
}) => {
  const { t } = useTranslation();

  const appliedList =
    uiOrderBumpsAplicados ?? checkout?.orderBumpsAplicados ?? [];

  const subtotalBase = uiSubtotal ?? checkout?.subtotal ?? 0;
  const subtotal = subtotalBase - (discount ?? 0);

  const config =
    checkout?.checkoutConfig?.config?.checkoutData?.checkoutHeaderModern;

  const recurrentLabel = useMemo(() => {
    switch (checkout?.plano?.billingConfig?.billingPeriodUnit) {
      case BillingPeriodUnit.Month:
        return t('perMonth');
      case BillingPeriodUnit.Week:
        return t('perWeek');
      case BillingPeriodUnit.Year:
        return t('perYear');
      case BillingPeriodUnit.Day:
        return t('perDay');
      default:
        return t('cashPayment');
    }
  }, [checkout?.plano?.billingConfig?.billingPeriodUnit, t]);

  const trialEnabled = !!checkout?.plano?.trialConfig?.isTrialEnabled;
  const trialDays = checkout?.plano?.trialConfig?.trialDays ?? null;
  const trialType = checkout?.plano?.trialConfig?.trialType ?? null;
  const trialPrice = checkout?.plano?.trialConfig?.trialPrice ?? 0;

  const hasFreeTrial =
    trialEnabled &&
    typeof trialDays === 'number' &&
    trialDays > 0 &&
    trialType === TrialType.WithPayment;

  const hasPaidTrial =
    trialEnabled &&
    typeof trialDays === 'number' &&
    trialDays > 0 &&
    trialType === TrialType.Paid;

  const recurringPrice = subtotal;
  const priceToday = hasPaidTrial ? trialPrice : subtotal;

  const recurringSummaryLines = useMemo(() => {
    if (!checkout?.recorrencia) return null;

    if (hasFreeTrial) {
      return {
        primary: t('freeForDays', { days: trialDays }),
        secondary: t('thenPrice', {
          price: formatCurrency(recurringPrice, checkout.moeda),
          interval: recurrentLabel,
        }),
      };
    }

    return {
      primary: t('payToday', {
        price: formatCurrency(priceToday, checkout.moeda),
      }),
      secondary: t('thenPrice', {
        price: formatCurrency(recurringPrice, checkout.moeda),
        interval: recurrentLabel,
      }),
    };
  }, [
    checkout?.recorrencia,
    checkout?.moeda,
    hasFreeTrial,
    priceToday,
    recurringPrice,
    recurrentLabel,
    t,
    trialDays,
  ]);

  return (
    <MobileSummaryBar>
      <div className="bar" onClick={() => setOpen(!open)}>
        <div className="title">
          <span className="c__cart-summary-title_o">{t('orderDetails')}</span>
          <Angle open={open}>▾</Angle>
        </div>

        {!open && (
          <div className="total">
            {!checkout?.recorrencia ? (
              <div>
                {config?.originalPrice > 0 &&
                  config?.originalPrice > subtotalBase && (
                    <span className="old">
                      {formatCurrency(
                        config?.originalPrice ?? 0,
                        checkout.moeda,
                      )}
                    </span>
                  )}{' '}
                <span className="now">
                  {formatCurrency(subtotal, checkout.moeda)}
                </span>
              </div>
            ) : (
              <div className="recurring-preview">
                <span className="primary">
                  {recurringSummaryLines?.primary}
                </span>
                <span className="secondary">
                  {recurringSummaryLines?.secondary}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {open && (
        <div className="body">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: 10,
              alignItems: 'center',
              paddingBottom: 8,
            }}
          >
            {config?.variant === 'photo' && (
              <img
                src={config?.image}
                alt={checkout?.produto?.nome}
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 8,
                  objectFit: 'cover',
                }}
              />
            )}

            <div>
              <div style={{ fontWeight: 700 }}>{checkout?.produto?.nome}</div>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                {config?.subtitle}
              </div>

              {false && (
                <div style={{ marginTop: 6 }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      overflow: 'hidden',
                      height: 28,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: '#f2f4f7',
                        width: 28,
                        cursor: 'pointer',
                      }}
                      onClick={() => setQty(clamp(qty - 1, 1, 99))}
                    >
                      −
                    </button>

                    <input
                      value={qty}
                      onChange={(e) =>
                        setQty(
                          clamp(parseInt(e.target.value || '1', 10), 1, 99),
                        )
                      }
                      style={{
                        width: 38,
                        border: 0,
                        textAlign: 'center',
                        outline: 0,
                      }}
                    />

                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: '#f2f4f7',
                        width: 28,
                        cursor: 'pointer',
                      }}
                      onClick={() => setQty(clamp(qty + 1, 1, 99))}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>

            {false && (
              <div style={{ fontWeight: 800 }}>
                {formatCurrency(subtotal, checkout.moeda)}
              </div>
            )}
          </div>

          {appliedList.length > 0 && (
            <div
              style={{
                padding: '6px 0 8px 0',
                marginBottom: 4,
                display: 'grid',
                gap: 4,
                fontSize: 13,
              }}
            >
              {appliedList.map((ob) => (
                <div
                  key={ob.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ color: 'var(--muted)' }}>
                    {ob?.produtoOferta?.nome}
                  </span>
                  <span>
                    {formatCurrency(
                      ob?.precos?.[0]?.valor ?? 0,
                      checkout.moeda,
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              borderTop:
                config?.originalPrice > 0 &&
                config?.originalPrice > subtotalBase
                  ? '1px solid var(--border)'
                  : 'none',
              paddingTop: 10,
              display: 'grid',
              gap: 6,
              fontSize: 14,
            }}
          >
            {config?.originalPrice > 0 &&
              config?.originalPrice > subtotalBase && (
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span style={{ color: 'var(--muted)' }}>
                    {t('originalPrice')}
                  </span>
                  <span
                    style={{ color: '#e63a3a', textDecoration: 'line-through' }}
                  >
                    {formatCurrency(config?.originalPrice, checkout.moeda)}
                    {checkout?.recorrencia ? ` ${recurrentLabel}` : ''}
                  </span>
                </div>
              )}

            {!checkout?.recorrencia ? (
              <>
                {config?.originalPrice > 0 &&
                  config?.originalPrice > subtotalBase && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <strong>{t('subtotal')}</strong>
                      <strong>
                        {formatCurrency(subtotalBase, checkout.moeda)}
                      </strong>
                    </div>
                  )}

                {discount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#f84f4f',
                    }}
                  >
                    <span>{t('discount')}</span>
                    <span>- {formatCurrency(discount, checkout.moeda)}</span>
                  </div>
                )}

                {checkout?.exibirEndereco && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ color: 'var(--muted)' }}>
                      {t('shippingCosts')}
                    </span>
                    <span>---</span>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid var(--border)',
                    paddingTop: 8,
                    paddingBottom: 10,
                    fontWeight: 800,
                  }}
                >
                  <span>{t('total')}</span>
                  <span>{formatCurrency(subtotal, checkout.moeda)}</span>
                </div>
              </>
            ) : (
              <>
                {discount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#f84f4f',
                    }}
                  >
                    <span>{t('discount')}</span>
                    <span>- {formatCurrency(discount, checkout.moeda)}</span>
                  </div>
                )}

                {checkout?.exibirEndereco && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ color: 'var(--muted)' }}>
                      {t('shippingCosts')}
                    </span>
                    <span>---</span>
                  </div>
                )}

                <div
                  style={{
                    paddingTop: 10,
                    paddingBottom: 10,
                    display: 'grid',
                    gap: 6,
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <strong>{recurringSummaryLines?.primary}</strong>
                  </div>

                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span style={{ color: 'var(--muted)', fontWeight: 600 }}>
                      {recurringSummaryLines?.secondary}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </MobileSummaryBar>
  );
};
